import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { message } from 'antd';
import Layout from './components/Layout';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import UsageManagement from './pages/UsageManagement';
import UserManagement from './pages/UserManagement';
import LinkManagement from './pages/LinkManagement';
import DomainManagement from './pages/DomainManagement';
import RuleManagement from './pages/RuleManagement';
import VisitorManagement from './pages/VisitorManagement';
import IPStats from './pages/IPStats';
import IPList from './pages/IPList';
import BlacklistManagement from './pages/BlacklistManagement';
import Statistics from './pages/Statistics';
import Settings from './pages/Settings';
import Profile from './pages/Profile';
import OperationLogs from './pages/OperationLogs';
import Billing from './pages/Billing';
import Docs from './pages/Docs';
import PixelConfig from './pages/PixelConfig';
import PostbackLogs from './pages/PostbackLogs';
import Referral from './pages/Referral';
import ReferralAdmin from './pages/ReferralAdmin';
// 测试/调试页面已移除，保持核心路由最小化
import ForbiddenPage from './pages/403';
import NotFoundPage from './pages/404';

import { PermissionGuard } from './components/PermissionGuard';
import { PermissionRoute } from './components/PermissionRoute';
import { ErrorBoundary } from './components/ErrorBoundary';
import { useAuthStore } from './stores/authStore';
import { usePermissionStore } from './stores/permissionStore';
import { authService } from './services/auth';
import { useDeviceConstantsStore } from './hooks/useDeviceConstantsStore';

function App() {
  const { isAuthenticated, user, token, logout } = useAuthStore();
  const location = useLocation();
  const { loadPermissions, loadMenuPermissions } = usePermissionStore();
  const deviceConstantsStore = useDeviceConstantsStore();

  // 初始化设备常量 - 只要页面加载就尝试从 localStorage 读取
  useEffect(() => {
    const initConstants = async () => {
      try {
        await deviceConstantsStore.initialize();
      } catch (error) {
        console.warn('设备常量初始化失败，但继续执行:', error);
      }
    };
    initConstants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 只执行一次

  // 初始化权限 - 只要有token就尝试加载权限
  useEffect(() => {
    if (token) {
      console.log('🔍 检测到token，开始加载权限...');
      const { permissions } = usePermissionStore.getState();
      
      // 强制重新加载权限，确保菜单正确显示
      console.log('🔄 强制重新加载权限');
      loadPermissions();
      loadMenuPermissions();
    } else {
      console.log('❌ 未检测到token，跳过权限加载');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]); // 只依赖token，函数引用在zustand中是稳定的

  // 检查token有效性 - 只在有token但无用户信息时检查
  useEffect(() => {
    const checkAuth = async () => {
      if (token && !user) {
        try {
          const profile = await authService.getProfile();
          // 更新用户信息到store
          useAuthStore.getState().updateUser(profile.user);
        } catch (error: any) {
          console.log('Token验证失败，清除认证信息');
          // 清除无效的认证信息
          useAuthStore.getState().logout();
          // 不显示错误消息，因为API拦截器已经处理了
        }
      }
    };

    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, user]); // 移除logout依赖，直接使用getState()调用

  // 如果未认证，显示登录或注册页面
  if (!isAuthenticated) {
    if (location.pathname === '/register') {
      return <Register />;
    }
    return <Login />;
  }

  return (
    <ErrorBoundary>
      <Layout>
        <Routes>
          {/* 默认路由重定向到仪表板 */}
          <Route index element={<Navigate to="/dashboard" replace />} />
          
          {/* 仪表板 */}
          <Route 
            path="dashboard"
            element={
              <PermissionRoute>
                <Dashboard />
              </PermissionRoute>
            } 
          />

          {/* 用量管理 */}
          <Route
            path="usage"
            element={
              <PermissionRoute>
                <UsageManagement />
              </PermissionRoute>
            }
          />

          {/* 计费中心 */}
          <Route
            path="billing"
            element={
              <PermissionRoute>
                <Billing />
              </PermissionRoute>
            }
          />
          
          {/* 用户管理 */}
          <Route 
            path="users" 
            element={
              <PermissionRoute>
                <UserManagement />
              </PermissionRoute>
            } 
          />
          
          {/* 链接管理 */}
          <Route 
            path="links" 
            element={
              <PermissionRoute>
                <LinkManagement />
              </PermissionRoute>
            } 
          />

          {/* 操作记录 */}
          <Route
            path="operation-logs"
            element={
              <PermissionRoute>
                <OperationLogs />
              </PermissionRoute>
            }
          />

          {/* 域名管理 */}
          <Route 
            path="domains" 
            element={
              <PermissionRoute>
                <DomainManagement />
              </PermissionRoute>
            } 
          />
          
          {/* 拦截规则管理 */}
          <Route 
            path="rules" 
            element={
              <PermissionRoute>
                <RuleManagement />
              </PermissionRoute>
            } 
          />
          
          {/* 访客记录 */}
          <Route 
            path="visitors" 
            element={
              <PermissionRoute>
                <VisitorManagement />
              </PermissionRoute>
            } 
          />

          {/* IP访问统计 */}
          <Route 
            path="ip-stats" 
            element={
              <PermissionRoute>
                <IPStats />
              </PermissionRoute>
            } 
          />

          {/* IP黑白名单 */}
          <Route 
            path="ip-list" 
            element={
              <PermissionRoute>
                <IPList />
              </PermissionRoute>
            } 
          />

          {/* 黑名单管理 */}
          <Route 
            path="blacklist" 
            element={
              <PermissionRoute>
                <BlacklistManagement />
              </PermissionRoute>
            } 
          />

          {/* 统计数据 */}
          <Route 
            path="statistics" 
            element={
              <PermissionRoute>
                <Statistics />
              </PermissionRoute>
            } 
          />
          
          {/* 系统设置 */}
          <Route 
            path="settings" 
            element={
              <PermissionRoute>
                <Settings />
              </PermissionRoute>
            } 
          />
          
          {/* 个人资料 */}
          <Route 
            path="profile" 
            element={
              <PermissionRoute>
                <Profile />
              </PermissionRoute>
            } 
          />
          
          {/* 帮助文档 - 所有用户都可以访问，不需要权限 */}
          <Route path="docs/:docType" element={<Docs />} />

          {/* 推广返佣 */}
          <Route 
            path="referral" 
            element={
              <PermissionRoute>
                <Referral />
              </PermissionRoute>
            } 
          />

          {/* 推广管理（管理端） */}
          <Route 
            path="referral-admin" 
            element={
              <PermissionRoute>
                <ReferralAdmin />
              </PermissionRoute>
            } 
          />

          {/* 增值功能 - 像素配置 */}
          <Route 
            path="pixel-config" 
            element={
              <PermissionRoute>
                <PixelConfig />
              </PermissionRoute>
            } 
          />

          {/* 增值功能 - 回传记录 */}
          <Route 
            path="postback-logs" 
            element={
              <PermissionRoute>
                <PostbackLogs />
              </PermissionRoute>
            } 
          />
          
          {/* 测试页已移除 */}
          
          {/* 错误页面 */}
          <Route path="403" element={<ForbiddenPage />} />
          <Route path="404" element={<NotFoundPage />} />
          
          {/* 通配符路由 - 处理刷新问题 */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Layout>
    </ErrorBoundary>
  );
}

export default App;